import parseReactNativeIPCMessage from '@/features/mobile-app-bridge/functions/parseReactNativeIPCMessage';
import useFeatureFlags from '@/features/feature-flags/hooks/useFeatureFlags';
import useHandleNotificationClick from '@/features/push/hooks/useHandleNotificationClick';
import useSyncPushDeviceMutation from '@/features/push/mutations/useSyncPushDeviceMutation';
import { useEffect } from 'react';
import { NotificationPayload } from 'firebase/messaging';
import { IpcResponseMessage } from '@/features/mobile-app-bridge/constants/ipc-response-message-schema';

declare global {
  interface Window {
    __TIRECLOUD_NATIVE_EVENTS__?: string[];
  }
}

export default function usePushNativeEventListener() {
  const featureFlags = useFeatureFlags();
  const { mutate: syncPushDevice } = useSyncPushDeviceMutation();
  const handleNotificationClick = useHandleNotificationClick();

  useEffect(() => {
    if (!featureFlags.push.enabled || !window.ReactNativeWebView) return;

    function handleNativeEventMessage(message: IpcResponseMessage) {
      if (message.type !== 'NATIVE_EVENT') return;
      if (message.event === 'NOTIFICATION_OPENED')
        handleNotificationClick(
          message.payload?.notification as NotificationPayload,
          message.payload?.data as Record<string, unknown>
        );
      if (message.event === 'FCM_TOKEN_REFRESHED') syncPushDevice();
    }

    function onMessage(event: MessageEvent) {
      const message = parseReactNativeIPCMessage(event);
      if (!message) return;
      handleNativeEventMessage(message);
    }

    window.addEventListener('message', onMessage);

    // Drain events that were injected before this listener mounted (cold start).
    const queued = window.__TIRECLOUD_NATIVE_EVENTS__ ?? [];
    window.__TIRECLOUD_NATIVE_EVENTS__ = [];
    for (const raw of queued) {
      const message = parseReactNativeIPCMessage({ data: raw } as MessageEvent);
      if (message) handleNativeEventMessage(message);
    }

    return () => window.removeEventListener('message', onMessage);
  }, [featureFlags.push.enabled, handleNotificationClick, syncPushDevice]);
}
